/**
 * The operator sign-out sequence, as a pure function over injected steps.
 *
 * Extracted from React so the ORDER is unit-testable: there is no DOM test
 * infrastructure anywhere in this workspace, and the order is the whole point.
 * Every teardown step must complete BEFORE the Clerk session is destroyed,
 * otherwise the next user to sign in on this browser inherits the previous
 * one's React Query cache, selected workspace and accent. Mobile already
 * treats that as a cross-account data-exposure rather than a cosmetic bug
 * (artifacts/mobile/hooks/useSignOut.ts); web had eight ad-hoc signOut() calls
 * and no teardown at all.
 */

export interface SignOutDeps {
  /** sessionStorage flag so the kiosk router sends us to /sign-in, not /player. */
  markExited(): void;
  /** Optional extra teardown that still needs a LIVE session (device revoke). */
  beforeSignOut?(): void | Promise<void>;
  /** Collapse a paired fullscreen player window (desktop). */
  closePlayer(): void;
  clearAccent(): void;
  clearPersistedBranding(): void;
  clearActiveWorkspaceId(): void;
  clearQueryCache(): void;
  signOut(options?: { redirectUrl?: string }): Promise<unknown> | unknown;
}

export interface SignOutOptions {
  redirectUrl?: string;
}

export async function runSignOut(
  deps: SignOutDeps,
  options?: SignOutOptions,
): Promise<void> {
  // 1. Flag first: a later step may redirect, and the guards read this.
  deps.markExited();

  // 2. Anything needing a live session and an unclearied cache. Swallowed on
  //    purpose: a failed device revoke must never strand the operator signed
  //    in with no way to try again.
  if (deps.beforeSignOut) {
    try {
      await deps.beforeSignOut();
    } catch {
      // Best-effort.
    }
  }

  // 3. Collapse the player window before the session dies.
  deps.closePlayer();

  // 4. Revert branding synchronously, before the sign-in page can paint.
  deps.clearAccent();
  deps.clearPersistedBranding();

  // 5-6. Drop everything tenant-scoped while the session still exists, so no
  //      component can refetch into the cache behind us.
  deps.clearActiveWorkspaceId();
  deps.clearQueryCache();

  // 7. Only now destroy the session.
  await deps.signOut(options?.redirectUrl ? { redirectUrl: options.redirectUrl } : undefined);
}
